import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllVariants,
  getSingleVariantById,
  createVariant,
  updateVariant,
  deleteVariant,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/variants";
import { v4 as uuidv4 } from "uuid";

// Thunks

export const fetchAllVariants = createAsyncThunk(
  "variants/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllVariants(restaurant_id, menu_id);
    if (res.status === "success") return res.data.variants;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleVariant = createAsyncThunk(
  "variants/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleVariantById(id);
    if (res.status === "success") return res.data.variant;
    return rejectWithValue(res.message);
  }
);

export const createNewVariant = createAsyncThunk(
  "variants/create",
  async (data, { rejectWithValue }) => {
    const res = await createVariant(data);
    if (res.status === "success") return res.data.variant;
    return rejectWithValue(res.message);
  }
);

export const updateExistingVariant = createAsyncThunk(
  "variants/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateVariant(id, data);
    if (res.status === "success") return res.data.variant;
    return rejectWithValue(res.message);
  }
);

export const deleteVariantById = createAsyncThunk(
  "variants/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteVariant(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  variants: [],
  itemVariants: [],
  singleVariant: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const variantSlice = createSlice({
  name: "variants",
  initialState,
  reducers: {
    clearVariantMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
    setItemVariants: (state, action) => {
      state.itemVariants = (action.payload || []).map((item) => ({
        ...item,
        unique_id: item.unique_id || uuidv4(),
      }));
    },

    removeItemVariant: (state, action) => {
      const id = action.payload;
      state.itemVariants = state.itemVariants.filter(
        (item) => item.unique_id !== id
      );
    },
    clearItemVariants: (state) => {
      state.itemVariants = [];
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllVariants.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllVariants.fulfilled, (state, action) => {
        state.getLoading = false;
        state.variants = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllVariants.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleVariant.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleVariant.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleVariant = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleVariant.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewVariant.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewVariant.fulfilled, (state, action) => {
        state.createLoading = false;
        state.variants.push(action.payload);
        state.itemVariants.push(action.payload);
        state.success = "Variant created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewVariant.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingVariant.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingVariant.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.variants = state.variants?.map((variant) =>
          variant.id === action.payload.id ? action.payload : variant
        );
        state.itemVariants = state?.itemVariants?.map((variant) =>
          variant.id === action.payload.id ? action.payload : variant
        );
        state.success = "Variant updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingVariant.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteVariantById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteVariantById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.variants = state.variants.filter(
          (item) => item.id !== action.payload
        );
        state.itemVariants &&= state.itemVariants.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Variant deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteVariantById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearVariantMessages,
  setItemVariants,
  removeItemVariant,
  clearItemVariants,
} = variantSlice.actions;
export default variantSlice.reducer;
